<script setup lang="ts">
import Button from '@/components/Button.vue'
import Message from '@/components/Message.vue'
import IconExclamation from '@/components/icons/IconExclamation.vue'
import IconTick from '@/components/icons/IconTick.vue'
import IconStatusError from '@/components/icons/IconStatusError.vue'
import IconStatusWarning from '@/components/icons/IconStatusWarning.vue'
import Select from '@/components/form-fields/Select.vue'
import Radio from '@/components/form-fields/Radio.vue'
import PurchaseLoading from '@/components/PurchaseLoading.vue'
import ParcelIcon from '@/icons/parcel.svg?component'
import TickIcon from '@/icons/tick.svg?component'

import formatMoney from '@/lib/formatters/money'
import * as DateUtils from '@/lib/utils/recharge-dates'

import type { Product } from '@/lib/types/Product'
import { computed } from 'vue'
import type { User } from '@/lib/types/User'

const props = withDefaults(
  defineProps<{
    price: number
    subscribePrice: number | null
    pricePer: {
      price: number
      unit: string
      subscribePrice: number | null
    } | null
    compareAtPrice: string | null
    allowSubscribe: boolean
    savePercent: number
    selectedSellingPlanId: string | null
    sellingPlanAllocations:
      | Product['variants'][number]['sellingPlanAllocations']
      | null
    quantity: number
    nextOrderDate: string | null
    when: 'now' | 'next'
    isLoggedIn: boolean
    loginLink: string
    userLoadState: string
    subscriptionToThisProduct: User['subscriptions'][number] | null
    customerPortalLink: string
    nextOrderLink: string
    purchaseState: string
    inTab: boolean
    showInlineVariantSelector: boolean
    variants: Product['variants']
    variantOptionLabel: string
    selectedVariantId: string
    available: boolean
    preSale?: boolean
    waitlistPageUrl?: string | null
    url: string
    immediateCheckout?: boolean
    hasDispensers: boolean
    isTrial: boolean
  }>(),
  { inTab: true },
)

const hasSubscriptions = computed(() => nextOrderDate.value !== null)

const nextOrderDate = computed(() => {
  if (props.nextOrderDate) {
    const date = DateUtils.parse(props.nextOrderDate)
    if (date) {
      return DateUtils.format(date)
    } else {
      return null
    }
  } else {
    return null
  }
})

const formattedNextOrderDate = computed(() =>
  nextOrderDate.value ? DateUtils.format(new Date(nextOrderDate.value)) : null,
)

const trackLoginNudge = () => {
  window.mixpanel.track('login_nudge_click')
}

const emit = defineEmits<{
  (e: 'when:changed', when: 'now' | 'next'): void
  (e: 'viewProduct'): void
  (e: 'variant:changed', variantId: string): void
  (e: 'purchase'): void
  (e: 'selectedSellingPlan:changed', sellingPlanId: string | null): void
}>()

const changeSellingPlan = (event: 'once' | 'subscribe') => {
  if (props.sellingPlanAllocations && props.sellingPlanAllocations.length > 0) {
    if (event === 'subscribe') {
      emit(
        'selectedSellingPlan:changed',
        props.sellingPlanAllocations[0].sellingPlan.id.toString(),
      )
    } else {
      emit('selectedSellingPlan:changed', null)
    }
  }
}
</script>

<template>
  <div>
    <div class="Stack Stack--small">
      <div class="Purchase__PriceLine">
        <div class="Purchase__PriceLineLeft">
          <span class="Purchase__Price">{{ formatMoney(price) }}</span>
          <span v-if="compareAtPrice" class="Purchase__PriceCompare">
            <s>{{ formatMoney(compareAtPrice) }}</s>
          </span>
        </div>
        <div class="Purchase__PriceLineRight">
          <span v-if="pricePer" class="Purchase__PricePer">
            {{ formatMoney(pricePer.price) }} / {{ pricePer.unit }}
          </span>
        </div>
      </div>
      <template v-if="userLoadState === 'LOADING'">
        <purchase-loading />
      </template>
      <template v-else-if="userLoadState === 'ERROR'">
        <Message mode="error">
          <template #icon>
            <icon-status-error />
          </template>
          <template #content>
            <p>
              Sorry, there was an error loading your options. Please refresh the
              page and try again.
            </p>
          </template>
        </Message>
      </template>
      <template v-else>
        <div class="Stack Stack--small">
          <div
            v-if="showInlineVariantSelector && Object.keys(variants).length > 1"
            class="Purchase__Options"
          >
            <span v-if="variantOptionLabel" class="Purchase__OptionsLabel">
              Choose {{ variantOptionLabel.toLowerCase() }}
            </span>
            <div class="Purchase__Option Purchase__Option--wide">
              <Select
                :value="selectedVariantId"
                :disabled="purchaseState === 'IN_PROGRESS'"
                @change="emit('variant:changed', $event)"
              >
                <option
                  v-for="variantId in Object.keys(variants)"
                  :key="variantId"
                  :value="variantId"
                >
                  {{ variants[variantId].title }} –
                  {{ formatMoney(variants[variantId].price) }}
                </option>
              </Select>
            </div>
          </div>
        </div>
        <div>
          <Select
            :value="selectedSellingPlanId !== null ? 'subscribe' : 'once'"
            @change="changeSellingPlan"
          >
            <option value="subscribe">Subscribe</option>
            <option value="once">Once only</option>
          </Select>
        </div>
        <div class="Purchase__FeatureList" v-if="!isTrial">
          <p class="Purchase__FeatureListItem">
            <ParcelIcon />
            <span>
              Your first order includes everything you need to get started{{
                hasDispensers ? ', including dispensers' : ''
              }}.
            </span>
          </p>
          <p class="Purchase__FeatureListItem">
            <TickIcon />
            <span>Delivered once</span>
          </p>
        </div>
        <div class="Purchase__FeatureList" v-else>
          <p class="Purchase__FeatureListItem">
            <ParcelIcon />
            <span>
              8 washes in the pack you can try before you commit to the full
              sized pack
            </span>
          </p>
          <p class="Purchase__FeatureListItem">
            <TickIcon />
            <span>Delivered once</span>
          </p>
        </div>
        <Radio
          v-if="hasSubscriptions"
          name="when"
          :value="when"
          :options="[
            {
              label: `Add to next order (${formattedNextOrderDate})`,
              value: 'next',
              link: { text: 'View', href: nextOrderLink },
            },
            { label: 'Add to basket to send now', value: 'now' },
          ]"
          :disabled="purchaseState === 'IN_PROGRESS'"
          @change="emit('when:changed', $event)"
        />
        <Message v-if="subscriptionToThisProduct" key="once-1" mode="dark">
          <template #icon>
            <icon-exclamation />
          </template>
          <template #content>
            <p>You are already subscribed to this product.</p>
            <p>
              <a
                :href="`${customerPortalLink}?redirect_to=${encodeURIComponent(
                  '/product-subscriptions',
                )}`"
                class="Link Link--inverted Link--bold"
              >
                Manage your subscription
              </a>
            </p>
          </template>
        </Message>
        <div class="Stack Stack--tiny">
          <template v-if="!available && preSale && waitlistPageUrl">
            <Message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>
                  This product is coming soon! Join the waitlist to be the first
                  to know when it launches.
                </p>
              </template>
            </Message>
            <Button :href="waitlistPageUrl" :modifiers="['block']">
              Join the waitlist
            </Button>
          </template>
          <template v-else-if="!available">
            <Message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>This product is not currently available</p>
              </template>
            </Message>
            <Button
              id="BIS_trigger"
              :modifiers="['block']"
              :extra-attributes="{ 'data-variant-id': selectedVariantId }"
            >
              Notify me when back in stock
            </Button>
          </template>
          <Button
            v-else
            :modifiers="['block']"
            :loading="purchaseState === 'IN_PROGRESS'"
            :disabled="purchaseState === 'COMPLETE'"
            @click="emit('purchase')"
          >
            <template v-if="purchaseState === 'COMPLETE'" #iconBefore>
              <icon-tick :animate="true" />
            </template>
            <template v-if="purchaseState === 'COMPLETE'"> Added </template>
            <template v-else-if="when === 'now' || !hasSubscriptions">
              <template v-if="immediateCheckout">Checkout now</template>
              <template v-else>Add to basket</template>
            </template>
            <template v-else> Add to order </template>
          </Button>
          <template v-if="purchaseState === 'ERROR'">
            <Message mode="error">
              <template #icon>
                <icon-status-error />
              </template>
              <template #content>
                <p>Sorry, there was an error. Please try again.</p>
              </template>
            </Message>
          </template>
          <p
            v-if="!isLoggedIn && inTab"
            class="Purchase__LoginNudge"
            @click="trackLoginNudge"
          >
            Already a subscriber?
            <a class="Link" :href="loginLink">Log in</a> to manage your orders
          </p>
          <p
            v-if="!inTab"
            class="Purchase__ViewLink"
            @click="emit('viewProduct')"
          >
            or
            <a class="Link" :href="url">view product page</a>
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
